import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { UsersService } from 'src/app/services/users/users.service';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { UserInfo } from '../../../models/user-info.model';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { KeycloakService } from 'src/app/services/keycloak/keycloak.service';
import { environment } from '../../../../environments/environment';
import { entAnimations } from 'src/app/animations';
import { ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { Error } from 'src/app/models/error.model';
import { ImageService } from 'src/app/services/profile-picture/images.service';
import { ImageUploadDialogComponent } from '../../image-upload-dialog/image-upload-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ImageDeleteDialogComponent } from '../../image-upload-dialog/image-delete-dialog/image-delete-dialog.component';
import { Subscription } from 'rxjs';
import { RouteStatusService } from 'src/app/services/maintenance/route-status.service';
import { CustomValidators } from 'src/app/utils/validators';
import { UserRightsHelper } from 'src/app/models/role.model';
import { UserUpdateComponent } from './user-update/user-update.component';
import { UpiService } from 'src/app/services/upi/upi.service';
import { Upi } from 'src/app/models/upi.model';
import { UserAvsUpdateValidationComponent } from './avs-update-validation/avs-update-validation.component';
import { LocalStorageService } from 'src/app/services/localStorage/localStorage.service';
import { UserPasswordChange } from 'src/app/models/user-password-change.model';

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: entAnimations,
  standalone: false,
})
export class UserComponent implements OnInit {
  //#region Error variables
  errorUpdatePwd: Error = { error: { message: '', code: '' } };
  errorGetUsers: Error = { error: { message: '', code: '' } };
  errorGetAvs: Error = { error: { message: '', code: '' } };
  //#endregion

  //#region Boolean variable
  isErrorUpdatePwd = false;
  isBusy = false;
  isBusyVerifyAvs = false;
  hasChanges = false;
  hasNotChanges = false;
  isBusyUpdatePwd = false;
  isErrorGetUsers = false;
  isLoading = false;
  dateOfBirthMatched = false;
  genderMatched = false;
  officialLastNameMatched = false;
  officialFirstNameMatched = false;
  lastNameMatched = false;
  firstNameMatched = false;
  loginMatched = false;
  emailMatched = false;
  avsMatched = false;
  isInfoCorrect = false;
  isPwdUpdated = false;
  canChangeName = false;
  isErrorGetAVS = false;
  //#endregion

  //#region Form variable
  securityform: FormGroup;
  updateform: FormGroup;
  upiInfoForm: FormGroup;
  //#endregion

  isPasswordChangeDisabled = false;
  isUploadImageDisabled = false;
  private subscription: Subscription = new Subscription();
  private passwordUpdateDate = new BehaviorSubject<Date>(null);
  lastPasswordModification$ = this.passwordUpdateDate.asObservable();

  //#region String variable
  userOid: string;
  selectedFileUrl: string | null = null;
  //#endregion

  //#region User variable
  selectedUser: UserInfo = new UserInfo();
  currentUser: UserInfo = new UserInfo();
  //#endregion

  //#region Spinner variable
  spinner = {
    color: 'primary',
    mode: 'indeterminate' as ProgressSpinnerMode,
    value: 50,
    diameter: 50,
  };

  spinnerUpdateUser = {
    color: 'primary',
    mode: 'indeterminate' as ProgressSpinnerMode,
    value: 50,
    diameter: 35,
  };
  //#endregion

  //#region Other variable
  dialogRef: MatDialogRef<unknown, unknown>;
  selectedTab = 0;
  //#endregion

  constructor(
    private _userService: UsersService,
    private _upiService: UpiService,
    private _translateService: TranslateService,
    private _activatedRoute: ActivatedRoute,
    private _formBuilder: FormBuilder,
    private _router: Router,
    private _keycloackService: KeycloakService,
    private _imageService: ImageService,
    private _dialog: MatDialog,
    private _routeStatusService: RouteStatusService,
    private _localStorageService: LocalStorageService
  ) {}

  ngOnInit() {
    this.isLoading = false;
    this.isBusy = false;
    this.selectedTab = Number(
      this._localStorageService.getItem('selectedTab') || 0
    );
    this._localStorageService.removeItem('selectedTab');

    this._activatedRoute.params.subscribe(params => {
      this.userOid = params['oid'];

      if (this.userOid == null) {
        this._router.navigate(['/users']);
      }

      this.getUser().then(() => {
        this.initializeUpdateForm();
      });
    });

    this.initializeSecurityForm();

    this._userService
      .getUserCredentials(this.userOid)
      .subscribe(value => this.passwordUpdateDate.next(value));
  }

  //#region User Info

  initializeSecurityForm() {
    this.securityform = this._formBuilder.group(
      {
        password: [
          '',
          [
            Validators.required,
            CustomValidators.passwordRestriction(),
            CustomValidators.passwordNameValidator(
              this.selectedUser.officialFirstName,
              this.selectedUser.officialLastName
            ),
            Validators.minLength(10),
            Validators.maxLength(50),
          ],
        ],
        passwordConfirmation: ['', [Validators.required]],
        mustChangePwd: [''],
      },
      { validators: [CustomValidators.confirmPasswords()] }
    );

    //Maintenance mode settings
    const urls = ['/password-change', '/image-upload'];
    this.subscription.add(
      this._routeStatusService
        .getRouteStatusForUrls(urls)
        .subscribe(statuses => {
          this.isPasswordChangeDisabled = statuses['/password-change'];
          this.isUploadImageDisabled = statuses['/image-upload'];
          if (this.isPasswordChangeDisabled) {
            this.securityform.disable();
          } else {
            this.securityform.enable();
          }
        })
    );
  }

  setDefaultValue(): void {
    // Password Default Value
    this.securityform
      .get('password')
      .setValidators([
        Validators.required,
        CustomValidators.passwordInterdiction(),
        CustomValidators.passwordNameValidator(
          this.selectedUser.officialFirstName,
          this.selectedUser.officialLastName
        ),
        CustomValidators.passwordSameAccountNameValidator(
          this.selectedUser.samAccountName.toLowerCase()
        ),
        CustomValidators.passwordRestriction(),
        Validators.minLength(10),
        Validators.maxLength(50),
      ]);

    this.securityform
      .get('passwordConfirmation')
      .setValidators([
        Validators.required,
        CustomValidators.passwordRestriction(),
        CustomValidators.passwordNameValidator(
          this.selectedUser.officialFirstName,
          this.selectedUser.officialLastName
        ),
        Validators.minLength(10),
        Validators.maxLength(50),
      ]);
  }

  fetchSecurityFormData(): UserPasswordChange {
    return {
      password: this.securityform.get('password').value,
      newPassword: this.securityform.get('password').value,
      mustChangePassword: this.securityform.get('mustChangePwd').value
        ? true
        : false,
    };
  }

  getErrorMessageUpdateForm(): string {
    return this.updateform.get('firstName').hasError('required')
      ? this._translateService.instant('ERROR.REQUIRED')
      : this.updateform.get('lastName').hasError('required')
        ? this._translateService.instant('ERROR.REQUIRED')
        : '';
  }

  getErrorMessage(): string {
    return this.securityform.get('password').hasError('required')
      ? this._translateService.instant('ERROR.REQUIRED')
      : this.securityform.get('password').hasError('passwordRestriction')
        ? this._translateService.instant('ERROR.COMPLEXITY')
        : this.securityform.get('password').hasError('oldpasswordmatch')
          ? this._translateService.instant('ERROR.INTERDICTION')
          : this.securityform.get('password').hasError('passwordContainName')
            ? this._translateService.instant('ERROR.CONTAINNAME')
            : this.securityform.get('password').hasError('minlength')
              ? this._translateService.instant('ERROR.MIN')
              : this.securityform
                    .get('password')
                    .hasError('passwordContainSamAccountName')
                ? this._translateService.instant('ERROR.CONTAINSAMACCOUNTNAME')
                : this.securityform.get('password').hasError('maxlength')
                  ? this._translateService.instant('ERROR.MAX')
                  : '';
  }

  checkPermission(): boolean {
    return this._imageService.checkPermissionAddImage(this.currentUser);
  }

  // Dialog add image
  openDialog(): void {
    const dialogRef = this._dialog.open(ImageUploadDialogComponent, {
      width: 'auto',
      height: 'auto',
      maxWidth: 'none',
      maxHeight: 'none',
      data: {
        userOid: this.userOid,
        isSelf: false,
        selectedFileUrl: this.selectedFileUrl,
        saveParentValue: value => {
          this.setParentValue(value);
        },
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined) this.selectedFileUrl = result;
    });
  }

  // Dilaog rmeove image
  openDialogDelete(): void {
    const dialogRef = this._dialog.open(ImageDeleteDialogComponent, {
      width: 'auto',
      height: 'auto',
      maxWidth: 'none',
      maxHeight: 'none',
      data: {
        userOid: this.userOid,
        isSelf: false,
        selectedFileUrl: this.selectedFileUrl,
        saveParentValue: value => {
          this.setParentValue(value);
        },
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined) this.selectedFileUrl = result;
    });
  }

  setParentValue(value): void {
    this.selectedFileUrl = value;
  }

  async getUser(): Promise<void> {
    this.errorGetUsers = { error: { message: '', code: '' } };
    this.isErrorGetUsers = false;

    try {
      this.currentUser = this._userService.currentUserValue;
      if (this.userOid === this.currentUser.oid) {
        this._router.navigate(['/self']);
      }

      this.isLoading = true;
      this.selectedUser = await lastValueFrom(
        this._userService.getUser(this.userOid)
      );

      this.selectedFileUrl = await lastValueFrom(
        this._imageService.getProfilePicture(this.selectedUser.oid)
      );

      this.setDefaultValue();
    } catch (error) {
      let customError: Error = { error: undefined };

      if (error.statusText == 'Unknown Error') {
        customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
      } else {
        if (error.error) {
          customError = error.error;
        }
        if (customError.error !== undefined) {
          if (customError.error.code == 'ERR403') {
            this._router.navigate(['/errors/error-403']);
          } else {
            this._router.navigate(['/errors/error-404']);
          }
        }
      }
      this.errorGetUsers = customError;
      this.isErrorGetUsers = true;
    } finally {
      this.isLoading = false;
    }
  }

  async changePwd(): Promise<void> {
    this.errorUpdatePwd = { error: { message: '', code: '' } };
    this.isBusy = false;
    this.isErrorUpdatePwd = false;
    this.isPwdUpdated = false;
    this.isBusyUpdatePwd = true;

    try {
      await lastValueFrom(
        this._userService.changeOtherUserPassword(
          this.selectedUser.oid,
          this.fetchSecurityFormData()
        )
      ).then(() => {
        this.isPwdUpdated = true;
        this.isBusyUpdatePwd = false;
      });
    } catch (error) {
      let customError: Error = { error: undefined };

      if (error.statusText == 'Unknown Error') {
        customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
      } else {
        if (error.error) {
          customError = error.error;
        }
        if (customError.error === undefined) {
          customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
        }
      }

      this.isErrorUpdatePwd = true;
      this.errorUpdatePwd = customError;
      this.isPwdUpdated = false;
      this.isBusyUpdatePwd = false;
    } finally {
      this.securityform.reset();
      this.securityform.controls['password'].setErrors(null);
      this.securityform.controls['passwordConfirmation'].setErrors(null);
      this.securityform.controls['password'].setValue('');
      this.securityform.controls['passwordConfirmation'].setValue('');
      this.passwordUpdateDate.next(new Date());
    }
  }

  async logout(): Promise<void> {
    this._keycloackService.logout(environment.logoutURL);
    this._userService.clearStorageAndLogout();
  }

  //#endregion

  //#region AVS Update

  initializeUpdateForm() {
    this.updateform = this._formBuilder.group(
      {
        avs: [{ value: this.selectedUser.avs, disabled: true }],
        officialFirstName: [
          { value: this.selectedUser.officialFirstName, disabled: true },
        ],
        officialLastName: [
          { value: this.selectedUser.officialLastName, disabled: true },
        ],
        firstName: [{ value: this.selectedUser.firstName, disabled: true }],
        lastName: [{ value: this.selectedUser.lastName, disabled: true }],
        eduvsMail: [{ value: this.selectedUser.officialemail, disabled: true }],
        eduvsLogin: [{ value: this.selectedUser.username, disabled: true }],
        dateOfBirth: [{ value: this.selectedUser.dateOfBirth, disabled: true }],
        gender: [{ value: this.selectedUser.gender, disabled: true }],
      },
      {
        validators: [
          CustomValidators.CheckUserFirstName(),
          CustomValidators.CheckUserLastName(),
        ],
      }
    );

    this.upiInfoForm = this._formBuilder.group({
      avs: [{ value: this.selectedUser.avs, disabled: true }],
      officialFirstName: [
        { value: this.selectedUser.officialFirstName, disabled: true },
      ],
      officialLastName: [
        { value: this.selectedUser.officialLastName, disabled: true },
      ],
      firstName: [{ value: this.selectedUser.firstName, disabled: true }],
      lastName: [{ value: this.selectedUser.lastName, disabled: true }],
      eduvsMail: [{ value: this.selectedUser.officialemail, disabled: true }],
      eduvsLogin: [{ value: this.selectedUser.username, disabled: true }],
      dateOfBirth: [{ value: this.selectedUser.dateOfBirth, disabled: true }],
      gender: [{ value: this.selectedUser.gender, disabled: true }],
    });
  }

  modifyAVS(): void {
    this.dialogRef = this._dialog.open(UserUpdateComponent, {
      panelClass: 'app-modify-avs-dialog',
      data: { currentUserAvs: this.avs },
    });

    this.dialogRef.afterClosed().subscribe(response => {
      this.errorGetAvs = { error: { message: '', code: '' } };
      if (response !== undefined) {
        this.updateAvsForm(response);
      }
    });
  }

  avsUpdateValidation(): void {
    this.dialogRef = this._dialog.open(UserAvsUpdateValidationComponent, {
      panelClass: 'app-modify-avs-dialog',
      data: {
        updatedUserFields: this.createFilteredFormGroup(),
        updatedUser: this.getNewUser(),
      },
    });

    this.dialogRef.afterClosed().subscribe(response => {
      if (response) {
        this._localStorageService.setItem('selectedTab', '1');
        window.location.reload();
      }
    });
  }

  hasGoodRole(): boolean {
    const currentUserRights = new UserRightsHelper(
      this.currentUser.roles,
      this.currentUser.orgs
    );

    const selectedUserRights = new UserRightsHelper(
      this.selectedUser.roles,
      this.selectedUser.orgs
    );

    //#region SUPPORT right
    const canChangeAvsInfoSupport = currentUserRights.isSupportICT();
    const canBeChangedAvsInfoSupport =
      selectedUserRights.canBeChangedAvsInfoSupport();

    //If CurrentUser is Support and SelectedUser is Teacher or StudentS2P
    if (canChangeAvsInfoSupport && canBeChangedAvsInfoSupport) {
      return true; //Can change AVS
    }
    //#endregion

    //#region GestionnaireEDUID or ChefdePoleS2P right
    const canChangeAvsInfoRole = currentUserRights.canChangeAvsInfo();
    const canBeChangedAvsInfo = selectedUserRights.canBeChangedAvsInfo();

    //If CurrentUser is GestionnaireEDUIDS2P or ChefdePoleS2P and SelectedUser is TeacherS2P or StudentS2P
    if (canChangeAvsInfoRole && canBeChangedAvsInfo) {
      return true; //Can change AVS
    }
    //#endregion

    return false; //Cannot modify AVS
  }

  defaultValue() {
    this.hasChanges = false;
    this.hasNotChanges = false;
    this.avsMatched = false;
    this.officialFirstNameMatched = false;
    this.officialLastNameMatched = false;
    this.firstNameMatched = false;
    this.lastNameMatched = false;
    this.dateOfBirthMatched = false;
    this.loginMatched = false;
    this.emailMatched = false;
    this.isInfoCorrect = false;
    this.genderMatched = false;
    this.canChangeName = false;
    this.isErrorGetAVS = false;
    this.resetForm();
  }

  resetForm() {
    this.updateform = this._formBuilder.group(
      {
        avs: [{ value: this.selectedUser.avs, disabled: true }],
        officialFirstName: [
          { value: this.selectedUser.officialFirstName, disabled: true },
        ],
        officialLastName: [
          { value: this.selectedUser.officialLastName, disabled: true },
        ],
        firstName: [{ value: this.selectedUser.firstName, disabled: true }],
        lastName: [{ value: this.selectedUser.lastName, disabled: true }],
        eduvsMail: [{ value: this.selectedUser.officialemail, disabled: true }],
        eduvsLogin: [{ value: this.selectedUser.username, disabled: true }],
        dateOfBirth: [{ value: this.selectedUser.dateOfBirth, disabled: true }],
        gender: [{ value: this.selectedUser.gender, disabled: true }],
      },
      {
        validators: [
          CustomValidators.CheckUserFirstName(),
          CustomValidators.CheckUserLastName(),
        ],
      }
    );
  }

  loginAndEmailValueChanged() {
    this.loginMatched = this.eduvsLogin !== this.updatedEduvsLogin;
    this.emailMatched = this.eduvsEmail !== this.updatedEduvsEmail;
  }

  avsValueChanged(upiModel: Upi): boolean {
    if (upiModel.avs !== this.avs) {
      this.updateform.get('avs').setValue(upiModel.avs);
      this.avsMatched = true;
    }
    if (upiModel.firstNames !== this.officialFirstName) {
      //Replace with new values
      this.updateform.get('officialFirstName').setValue(upiModel.firstNames);
      this.officialFirstNameMatched = true; //Show new value
    }
    if (this.updatedOfficialFirstName.split(' ').length > 1) {
      //if its the case, the field are editable
      this.updateform.get('firstName').enable();
    } else if (this.firstName !== this.updatedOfficialFirstName) {
      this.updateform.get('firstName').setValue(upiModel.firstNames);
      this.firstNameMatched = true;
    }

    //Check if actual firstname are not equal to new firstnames
    if (upiModel.officialName !== this.officialLastName) {
      //Replace with new values
      this.updateform.get('officialLastName').setValue(upiModel.officialName);
      this.officialLastNameMatched = true;
    }
    //Check if there is more than 1 name
    if (this.updatedOfficialLastName.split(' ').length > 1) {
      //if its the case, the field are editable
      this.updateform.get('lastName').enable();
    } else if (this.lastName !== this.updatedOfficialLastName) {
      this.updateform.get('lastName').setValue(upiModel.officialName);
      this.lastNameMatched = true;
    }

    if (this.lastNameMatched || this.firstNameMatched) {
      this.updateUserLogin();
    }

    const date = upiModel.dateOfBirth.split('-');
    upiModel.dateOfBirth =
      date[2].substring(0, 2) + '.' + date[1] + '.' + date[0];

    if (upiModel.dateOfBirth !== this.dateOfBirth) {
      this.updateform.get('dateOfBirth').setValue(upiModel.dateOfBirth);
      this.dateOfBirthMatched = true;
    }
    if (upiModel.sexAcronym !== this.gender) {
      this.updateform.get('gender').setValue(upiModel.sexAcronym);
      this.genderMatched = true;
    }

    return (this.hasChanges =
      this.avsMatched ||
      this.genderMatched ||
      this.dateOfBirthMatched ||
      this.officialLastNameMatched ||
      this.officialFirstNameMatched ||
      this.firstNameMatched ||
      this.lastNameMatched);
  }

  createFilteredFormGroup() {
    const filteredFields = {};
    // Créer de nouveaux FormControl avec les valeurs des contrôles existants
    if (this.avsMatched)
      filteredFields['avs'] = new FormControl(this.updatedAvs);
    if (this.officialFirstNameMatched)
      filteredFields['officialFirstName'] = new FormControl(
        this.updatedOfficialFirstName
      );
    if (this.officialLastNameMatched)
      filteredFields['officialLastName'] = new FormControl(
        this.updatedOfficialLastName
      );
    if (this.firstNameMatched)
      filteredFields['firstName'] = new FormControl(this.updatedFirstName);
    if (this.lastNameMatched)
      filteredFields['lastName'] = new FormControl(this.updatedLastName);
    if (this.emailMatched)
      filteredFields['eduvsMail'] = new FormControl(this.updatedEduvsEmail);
    if (this.loginMatched)
      filteredFields['eduvsLogin'] = new FormControl(this.updatedEduvsLogin);
    if (this.dateOfBirthMatched)
      filteredFields['dateOfBirth'] = new FormControl(this.updatedDateOfBirth);
    if (this.genderMatched)
      filteredFields['gender'] = new FormControl(this.updatedGender);

    // Créer un nouveau FormGroup avec les nouveaux FormControl
    return filteredFields;
  }

  getNewUser(): UserInfo {
    const user = { ...this.selectedUser };
    user.officialFirstName = this.updatedOfficialFirstName;
    user.officialLastName = this.updatedOfficialLastName;
    user.firstName = CustomValidators.capitalizeFirstLetter(
      this.updatedFirstName
    );
    user.lastName = CustomValidators.capitalizeFirstLetter(
      this.updatedLastName
    );
    user.dateOfBirth = this.updatedDateOfBirth;
    user.avs = this.updatedAvs;
    user.gender = this.updatedGender;
    return user;
  }

  updateAvsForm(userUpdated) {
    this.hasNotChanges = !this.avsValueChanged(userUpdated);
    this.canChangeName = this.hasUserMultipleName(userUpdated);
    if (this.hasChanges) {
      this.updateUserLoginForm();
    }
  }

  updateUserLoginForm() {
    this.firstNameMatched = this.updatedFirstName != this.firstName;
    this.lastNameMatched = this.updatedLastName != this.lastName;
    if (this.updateform.valid) {
      this.updateUserLogin();
    }
  }

  async updateUserLogin() {
    const newUsername = await lastValueFrom(
      this._userService.getNewUsername(this.getNewUser())
    );
    this.updateform.get('eduvsLogin').setValue(newUsername);
    this.updateform.get('eduvsMail').setValue(newUsername + '@edu.vs.ch');
    this.loginAndEmailValueChanged();
  }

  async verifyUpiInfo() {
    this.isBusyVerifyAvs = true;
    this.isInfoCorrect = false;
    this.isErrorGetAVS = false;
    this.errorGetAvs = { error: { message: '', code: '' } };
    //this.defaultValue();

    let upiModel: Upi;
    try {
      upiModel = await lastValueFrom(
        this._upiService.getUpiInfoByAVS(this.avs)
      );
      upiModel.avs = this.avs;
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      upiModel.sex === 1
        ? (upiModel.sexAcronym = 'M')
        : (upiModel.sexAcronym = 'F');
      this.updateAvsForm(upiModel);
    } catch (error) {
      let customError: Error = {
        error: { message: 'Unknown Error', code: 'AVS' },
      };
      if (error.statusText == 'Unknown Error') {
        customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
      } else {
        if (error.status === 400) {
          customError = { error: { message: 'Unknown Error', code: 'ERR19' } };
        }
        if (error.status === 500) {
          customError = { error: { message: 'Unknown Error', code: 'ERR19' } };
        }
      }
      this.errorGetAvs = customError;
      this.isErrorGetAVS = true;
    } finally {
      this.isBusyVerifyAvs = false;
    }
  }

  hasUserMultipleName(userUpdated: Upi): boolean {
    const hasMultipleFirstName = userUpdated.firstNames.split(' ').length > 1;
    const hasMultipleLastName = userUpdated.officialName.split(' ').length > 1;
    if (hasMultipleFirstName) {
      this.updateform.get('firstName').enable();
    }
    if (hasMultipleLastName) {
      this.updateform.get('lastName').enable();
    }

    if (hasMultipleFirstName || hasMultipleLastName) {
      return true;
    }

    return false;
  }

  //#region Get Form properties
  get avs(): string {
    return this.upiInfoForm.get('avs').value;
  }

  get officialFirstName(): string {
    return this.upiInfoForm.get('officialFirstName').value;
  }

  get officialLastName(): string {
    return this.upiInfoForm.get('officialLastName').value;
  }

  get firstName(): string {
    return this.upiInfoForm.get('firstName').value;
  }

  get lastName(): string {
    return this.upiInfoForm.get('lastName').value;
  }

  get dateOfBirth(): string {
    return this.upiInfoForm.get('dateOfBirth').value;
  }

  get gender(): string {
    return this.upiInfoForm.get('gender').value;
  }

  get eduvsLogin(): number {
    return this.upiInfoForm.get('eduvsLogin').value;
  }

  get eduvsEmail(): number {
    return this.upiInfoForm.get('eduvsMail').value;
  }

  get updatedAvs(): string {
    return this.updateform.get('avs').value;
  }

  get updatedOfficialFirstName(): string {
    return this.updateform.get('officialFirstName').value;
  }

  get updatedOfficialLastName(): string {
    return this.updateform.get('officialLastName').value;
  }

  get updatedFirstName(): string {
    return this.updateform.get('firstName').value;
  }

  get updatedLastName(): string {
    return this.updateform.get('lastName').value;
  }

  get updatedDateOfBirth(): string {
    return this.updateform.get('dateOfBirth').value;
  }

  get updatedGender(): string {
    return this.updateform.get('gender').value;
  }

  get updatedEduvsLogin(): number {
    return this.updateform.get('eduvsLogin').value;
  }

  get updatedEduvsEmail(): number {
    return this.updateform.get('eduvsMail').value;
  }
  //#endregion

  //#endregion
}
